import styled, { css, keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";
import { Container as BarContainer } from "../../../../components/SaveBar/styles";

export const Container = styled.div<{ $flow?: boolean }>`
  position: relative;
  min-height: 100dvh;

  /* In the creation flow: a column, so the flow's fixed bar sits at the bottom of short pages */
  ${props =>
    props.$flow &&
    css`
      display: flex;
      flex-direction: column;
    `}

  /* The green glow starts right at the top of the screen (there's no header) */
  background:
    radial-gradient(60% 440px at 50% 0%, rgba(87, 242, 139, 0.14) 0%, rgba(87, 242, 139, 0) 70%),
    var(--color-neutral-darkness);

  .plus-cta {
    width: 100%;
    padding: 1rem;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
    color: var(--color-neutral-darkness);
    background: var(--color-brand-plus);
    border-radius: 100px;
    transition: filter 0.15s;

    &:hover {
      filter: brightness(1.08);
    }
  }
`;

/* A light sweeping across the CTA every few seconds */
const ctaShine = keyframes`
  0%,
  60% {
    background-position: 130% 0;
  }
  100% {
    background-position: -30% 0;
  }
`;

/* The subscribe CTA, pinned to the bottom: a neutral bar so the purple button (the page's only one) is
   where the eye lands; the plan and its saving sit as a green chip over the price */
export const StickyBar = styled.div`
  position: fixed;
  /* Between the sidebar and the Keepo's preview, when the screen is a step of creating one */
  right: var(--app-preview-width, 0px);
  bottom: var(--app-bottom-bar-offset, 0px);
  left: var(--app-sidebar-width, 0px);
  z-index: 90;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  width: min(720px, calc(100% - var(--app-sidebar-width, 0px) - var(--app-preview-width, 0px)));
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
  margin: 0 auto;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.55);

  .summary {
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
  }

  /* A small chip in the PLUS colors, under the price */
  .plan {
    order: 1;
    align-self: flex-start;
    padding: 0.0625rem 0.375rem;
    font-family: Inter, sans-serif;
    font-size: 0.5625rem;
    font-weight: 900;
    line-height: 0.8125rem;
    color: var(--color-brand-plus);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    background: rgba(87, 242, 139, 0.15);
    border-radius: 100px;
  }

  .price {
    flex-shrink: 0;
    font-family: Inter, sans-serif;
    font-size: 1.375rem;
    font-weight: 800;
    color: var(--color-neutral-white);

    small {
      margin-left: 0.125rem;
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--color-neutral-150);
    }
  }

  button.plus-cta {
    display: inline-flex;
    flex-shrink: 0;
    gap: 0.5rem;
    align-items: center;
    width: auto;
    padding: 1rem 1.75rem;
    font-size: 0.9375rem;
    color: var(--color-neutral-white);
    background: linear-gradient(110deg, #4b10ff 40%, #8a63ff 50%, #4b10ff 60%);
    background-size: 250% 100%;
    box-shadow: 0 10px 30px rgba(75, 16, 255, 0.45);
    animation: ${ctaShine} 3.5s ease-in-out infinite;

    .arrow {
      transition: transform 0.15s;
    }

    &:hover .arrow {
      transform: translateX(3px);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
      animation: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    button.plus-cta {
      animation: none;
    }
  }

  /* On desktop the bar lines up with the page's body (656px) */
  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    width: min(calc(720px - 4rem), calc(100% - var(--app-sidebar-width, 0px) - var(--app-preview-width, 0px) - 4rem));
    padding: 1rem 1rem 1rem 1.75rem;
    border-bottom: 0;
    border-radius: 1.25rem 1.25rem 0 0;
  }

  /* Phones: a floating pill right above the floating bottom nav, with the same side gaps */
  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    right: 20px;
    bottom: calc(var(--app-bottom-nav-height, 0px) - 8px);
    left: 20px;
    gap: 0.5rem;
    width: auto;
    padding: 0.5rem 0.5rem 0.5rem 1.25rem;
    border-radius: 100px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);

    .plan {
      display: none;
    }

    .price {
      font-size: 1rem;
    }

    button.plus-cta {
      min-width: 0;
      padding: 0.75rem 1rem;
      font-size: 0.8125rem;
      white-space: nowrap;
    }
  }
`;

/* The page's content: the app's contained width (same as the sticky bar), centered */
export const Content = styled.main<{ $flow?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* The app's body width (the sticky bar's content) */
  max-width: 656px;
  /* Room for the fixed bar below (about 4.5rem tall) and a bit of air, no more */
  padding: 1.5rem 0 6rem;
  margin: 0 auto;

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    /* The app already leaves the bottom nav's room; the bar sits right on it */
    padding: 1.5rem 20px 5.5rem;
  }

  /* In the creation flow there's no floating bar to leave room for (on phones too: the media rule above
     comes out after this one, so it's undone in its own media rule) */
  ${props =>
    props.$flow &&
    css`
      padding-bottom: 0;

      @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
        padding-bottom: 0;
      }
    `}
`;

/* No header: just the way back */
export const TopLinks = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 2rem;

  button {
    display: inline-flex;
    gap: 0.375rem;
    align-items: center;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-150);
    transition: color 0.15s;

    &:hover {
      color: var(--color-neutral-white);
    }
  }

  /* On the right, even when there's no way back on the left */
  .save-draft {
    margin-left: auto;
    text-decoration: underline;
    text-underline-offset: 4px;

    &:disabled {
      cursor: progress;
      opacity: 0.6;
    }
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    padding: 1rem 20px;
  }
`;

/* Centered pitch over a soft green glow, with the plans right under it */
export const Hero = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  h1 {
    max-width: 620px;
    margin-top: 1rem;
    font-family: Inter, sans-serif;
    font-size: 2.125rem;
    font-weight: 800;
    line-height: 2.5rem;
    letter-spacing: -0.02em;
    color: var(--color-neutral-white);
  }

  .subtitle {
    max-width: 520px;
    margin-top: 0.75rem;
    font-family: Inter, sans-serif;
    font-size: 1rem;
    line-height: 1.5rem;
    color: rgba(255, 255, 255, 0.72);
  }

  .secure {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-top: 1.25rem;
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    color: var(--color-neutral-150);
  }

  .info {
    margin-top: 0.5rem;
    font-family: Inter, sans-serif;
    font-size: 0.6875rem;
    color: var(--color-neutral-light);
  }

  .error {
    margin-top: 1rem;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    color: var(--color-feedback-danger);
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    h1 {
      font-size: 1.625rem;
      line-height: 2rem;
    }

    .subtitle {
      font-size: 0.9375rem;
      line-height: 1.375rem;
    }
  }
`;

/* The two plans as selectable cards: both prices in sight, the yearly one's saving called out */
export const Plans = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 440px;
  margin-top: 1.75rem;
  text-align: left;
`;

export const PlanCard = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 1.125rem 1.25rem;
  text-align: left;
  background: ${props => (props.$selected ? "rgba(38, 42, 52, 0.85)" : "rgba(38, 42, 52, 0.5)")};
  border: ${props => (props.$selected ? "2px solid var(--color-brand-plus)" : "1px solid var(--color-neutral-base)")};
  border-radius: 1rem;
  transition:
    border-color 0.15s,
    background-color 0.15s;

  &:hover {
    border-color: ${props => (props.$selected ? "var(--color-brand-plus)" : "var(--color-neutral-light)")};
  }

  .radio {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: ${props =>
      props.$selected ? "5px solid var(--color-brand-plus)" : "2px solid var(--color-neutral-light)"};
    border-radius: 100%;
  }

  .name {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
    font-family: Inter, sans-serif;

    b {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      font-size: 1rem;
      font-weight: 700;
      color: var(--color-neutral-white);
    }

    > span {
      font-size: 0.75rem;
      color: var(--color-neutral-150);
    }

    s {
      color: var(--color-neutral-light);
    }
  }

  .discount {
    padding: 0.125rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 800;
    color: var(--color-brand-plus);
    background: rgba(87, 242, 139, 0.15);
    border-radius: 100px;
  }

  .value {
    flex-shrink: 0;
    font-family: Inter, sans-serif;
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--color-neutral-white);

    small {
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--color-neutral-150);
    }
  }

  .save {
    position: absolute;
    top: -11px;
    right: 16px;
    padding: 0.1875rem 0.625rem;
    font-family: Inter, sans-serif;
    font-size: 0.625rem;
    font-weight: 800;
    color: var(--color-neutral-darkness);
    text-transform: uppercase;
    background: var(--color-brand-plus);
    border-radius: 100px;
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    .value {
      font-size: 1.25rem;
    }
  }
`;

/* What PLUS brings: icon + title + one line each, two per row (one on phones) */
export const Benefits = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  /* Fades out at both ends */
  &::before {
    width: 100%;
    height: 1px;
    margin: 2.5rem 0 2rem;
    content: "";
    background: linear-gradient(90deg, transparent, var(--color-neutral-base), transparent);
  }

  h2 {
    margin-bottom: 1.75rem;
    font-family: Inter, sans-serif;
    font-size: 1.125rem;
    font-weight: 800;
    color: var(--color-neutral-white);
  }

  ul {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.75rem 2.5rem;
    width: 100%;
  }

  li {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 1rem;
    line-height: 1;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 10px;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    font-family: Inter, sans-serif;

    b {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      font-size: 0.9375rem;
      font-weight: 700;
      line-height: 1.25rem;
      color: var(--color-neutral-white);
    }

    > span {
      font-size: 0.8125rem;
      line-height: 1.1875rem;
      color: var(--color-neutral-150);
    }
  }

  /* A small yellow detail (the AI sparkle's yellow): "new" stands apart from the PLUS green */
  .new {
    padding: 0 0.25rem;
    font-size: 0.4375rem;
    font-weight: 900;
    line-height: 0.6875rem;
    color: var(--color-neutral-darkness);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: #ffd00d;
    border-radius: 100px;
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    ul {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }
`;

/* In the creation flow: the steps' header (way back, title) and their progress, over the green glow */
export const FlowHeader = styled.div`
  position: relative;
  z-index: 1;

  .save-draft {
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-150);
    text-decoration: underline;
    text-underline-offset: 4px;
    transition: color 0.15s;

    &:hover {
      color: var(--color-neutral-white);
    }

    &:disabled {
      cursor: progress;
      opacity: 0.6;
    }
  }
`;

/* Above the creation flow's bar: what publishing without PLUS leaves off (the Keepo's pages) */
export const FlowNotice = styled.p`
  margin-bottom: 0.875rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--color-neutral-light);
`;

/* The creation flow's bar: on phones, subscribing takes the whole width and the way on without PLUS
   goes under it, centered */
export const FlowBar = styled(BarContainer)`
  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    flex-direction: column-reverse;
    gap: 0.875rem;

    && > * {
      flex: none;
      width: 100%;
      text-align: center;
    }
  }
`;
